import React from "react";
import { Play, FileText, BookOpen, Folder, Pencil, Trash2, Download, ExternalLink, X } from "lucide-react";
import { API } from "../../lib/api";

const ESTILO = {
  video: { top: "from-sky-100 to-sky-50", fg: "text-sky-600", badge: "bg-sky-50 text-sky-700", icon: Play, label: "Vídeo" },
  tutorial: { top: "from-emerald-100 to-emerald-50", fg: "text-emerald-600", badge: "bg-emerald-50 text-emerald-700", icon: BookOpen, label: "Tutorial" },
  manual: { top: "from-rose-100 to-rose-50", fg: "text-rose-500", badge: "bg-amber-50 text-amber-700", icon: FileText, label: "Manual" },
  otros: { top: "from-slate-100 to-slate-50", fg: "text-slate-500", badge: "bg-slate-100 text-slate-600", icon: Folder, label: "Otros" },
};

export const ficheroUrl = (r, descargar) => `${API}/descargas/${r.id}/fichero${descargar ? "?descargar=true" : ""}`;
const tam = (b) => (b == null ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1).replace(".", ",")} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const esVideo = (r) => (r.content_type || "").startsWith("video/");
const esPdf = (r) => (r.content_type || "").includes("pdf");

export function embedUrl(url) {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

export const sePuedeVer = (r) => (r.es_enlace ? !!embedUrl(r.url) : esVideo(r) || esPdf(r));

export function TarjetaRecurso({ r, admin, onVer, onEditar, onBorrar }) {
  const e = ESTILO[r.tipo] || ESTILO.otros;
  const Icon = r.es_enlace || esVideo(r) ? Play : e.icon;
  const ver = sePuedeVer(r);
  return (
    <div className="card overflow-hidden flex flex-col transition-shadow hover:shadow-md" data-testid={`recurso-${r.id}`}>
      <button onClick={() => (ver ? onVer(r) : window.open(r.es_enlace ? r.url : ficheroUrl(r, true), "_blank"))}
              className={`h-36 bg-gradient-to-b ${e.top} flex items-center justify-center group`} data-testid={`recurso-portada-${r.id}`}>
        <Icon size={40} className={`${e.fg} transition-transform group-hover:scale-110`} />
      </button>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${e.badge}`}>{e.label}</span>
          {admin && (
            <span className="flex gap-2 text-slate-400">
              <button onClick={() => onEditar(r)} className="hover:text-slate-700" title="Editar" data-testid={`recurso-editar-${r.id}`}><Pencil size={14} /></button>
              <button onClick={() => onBorrar(r)} className="hover:text-red-600 text-red-400" title="Borrar" data-testid={`recurso-borrar-${r.id}`}><Trash2 size={14} /></button>
            </span>
          )}
        </div>
        <div className="font-semibold leading-snug">{r.titulo}</div>
        {r.descripcion && <div className="text-xs text-slate-500 mt-1">{r.descripcion}</div>}
        <div className="text-[11px] font-mono text-slate-400 mt-1 truncate">{r.es_enlace ? r.url : `${r.filename} · ${tam(r.size)}`}</div>
        <div className="mt-auto pt-3 flex gap-2">
          {ver && <button onClick={() => onVer(r)} className="btn btn-outline btn-sm flex-1" data-testid={`recurso-ver-${r.id}`}><Play size={12} /> Ver</button>}
          {r.es_enlace
            ? <a href={r.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm flex-1" data-testid={`recurso-abrir-${r.id}`}><ExternalLink size={12} /> Abrir</a>
            : <a href={ficheroUrl(r, true)} className="btn btn-primary btn-sm flex-1" data-testid={`recurso-descargar-${r.id}`}><Download size={12} /> Descargar</a>}
        </div>
      </div>
    </div>
  );
}

export function Visor({ r, onClose }) {
  const emb = r.es_enlace ? embedUrl(r.url) : null;
  return (
    <div className="fixed inset-0 z-[90] w-screen h-screen bg-slate-950 flex flex-col" data-testid="recurso-visor">
      <div className="flex items-center justify-between px-4 py-2 text-white">
        <div className="font-semibold truncate">{r.titulo}</div>
        <button onClick={onClose} className="h-9 w-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20" data-testid="recurso-visor-cerrar"><X size={18} /></button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center p-2">
        {emb ? <iframe title={r.titulo} src={emb} className="w-full h-full rounded-lg" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
          : esVideo(r) ? <video src={ficheroUrl(r)} controls autoPlay className="max-w-full max-h-full rounded-lg" data-testid="recurso-video" />
            : <iframe title={r.titulo} src={ficheroUrl(r)} className="w-full h-full rounded-lg bg-white" />}
      </div>
    </div>
  );
}
